import type { Auth0Client } from '@auth0/auth0-spa-js';

/**
 * Signing in to the console with Auth0 — the only way in.
 *
 * Auth0 is used once per sign-in, to prove who someone is: the browser runs Auth0's login (PKCE, no
 * secret), and the access token it gets back is exchanged at `POST /api/auth/auth0` for the console's
 * ordinary HTTP-only session cookie. After that nothing here holds a token — every request rides on
 * the cookie, so no guard, stream or admin check needs to know where the session came from.
 *
 * **Nothing credential-shaped is written to `localStorage`.** The Auth0 client keeps its cache in
 * memory and asks for no refresh token: this console shares an origin with the sources hub's operator
 * console at `/sources-hub/admin`, and anything persisted here is readable by that app's JavaScript —
 * and the reverse. The one thing stored is a boolean saying the session came from Auth0, so signing
 * out also ends the Auth0 session (otherwise "Continue with Auth0" would sign the same person
 * straight back in, which is not what signing out means).
 */

export interface SignInOptions {
  /** Null where the deployment has no Auth0 configured — nobody can sign in there. */
  auth0: { domain: string; clientId: string; audience: string; organization: string | null } | null;
}

const AUTH0_SESSION_FLAG = 'cdr-memory:signed-in-with-auth0';
const CALLBACK_PATH = '/login';

let client: Auth0Client | null = null;
let options: Promise<SignInOptions> | null = null;

/** How this deployment signs people in — fetched once per page load. */
export function signInOptions(): Promise<SignInOptions> {
  options ??= fetch('/api/auth/options', { credentials: 'include' })
    .then((res) => (res.ok ? (res.json() as Promise<SignInOptions>) : { auth0: null }))
    .catch(() => ({ auth0: null }));
  return options;
}

async function auth0Client(config: NonNullable<SignInOptions['auth0']>): Promise<Auth0Client> {
  if (client) return client;
  // On demand, so the SDK is not in the bundle every signed-in page load pays for.
  const { createAuth0Client } = await import('@auth0/auth0-spa-js');
  client = await createAuth0Client({
    domain: config.domain,
    clientId: config.clientId,
    cacheLocation: 'memory',
    useRefreshTokens: false,
    authorizationParams: {
      audience: config.audience,
      ...(config.organization ? { organization: config.organization } : {}),
      redirect_uri: `${window.location.origin}${CALLBACK_PATH}`,
      // `email` is what the account is matched on; the Post-Login Action copies it into the token.
      scope: 'openid profile email',
    },
  });
  return client;
}

/** Sends the browser to Auth0's login page. */
export async function startAuth0SignIn(): Promise<void> {
  const { auth0 } = await signInOptions();
  if (!auth0) throw new Error('Auth0 sign-in is not configured on this deployment');
  await (await auth0Client(auth0)).loginWithRedirect();
}

/** Whether this page load is Auth0 handing the browser back after login. */
export function isAuth0Callback(): boolean {
  const params = new URLSearchParams(window.location.search);
  return window.location.pathname === CALLBACK_PATH && params.has('state') && (params.has('code') || params.has('error'));
}

/**
 * Finishes an Auth0 login and returns the access token to exchange.
 *
 * The query string is stripped straight away, so a reload does not re-submit a spent authorization
 * code — which Auth0 correctly refuses, and which would read as a broken login.
 */
export async function completeAuth0SignIn(): Promise<string> {
  const { auth0 } = await signInOptions();
  if (!auth0) throw new Error('Auth0 sign-in is not configured on this deployment');
  const params = new URLSearchParams(window.location.search);
  window.history.replaceState({}, '', CALLBACK_PATH);
  if (params.has('error')) {
    throw new Error(params.get('error_description') || params.get('error') || 'Auth0 sign-in was cancelled');
  }
  const instance = await auth0Client(auth0);
  await instance.handleRedirectCallback(`${window.location.origin}${CALLBACK_PATH}?${params.toString()}`);
  return instance.getTokenSilently();
}

export function markAuth0Session(): void {
  try {
    localStorage.setItem(AUTH0_SESSION_FLAG, '1');
  } catch {
    // Storage blocked: signing out then leaves the Auth0 session open, which only costs a click.
  }
}

/**
 * Ends the Auth0 session too, when the console session came from it. Redirects the browser, so it is
 * the last thing sign-out does; returns false when there was nothing to end.
 */
export async function endAuth0Session(): Promise<boolean> {
  let flagged = false;
  try {
    flagged = localStorage.getItem(AUTH0_SESSION_FLAG) === '1';
    localStorage.removeItem(AUTH0_SESSION_FLAG);
  } catch {
    flagged = false;
  }
  if (!flagged) return false;
  const { auth0 } = await signInOptions();
  if (!auth0) return false;
  await (await auth0Client(auth0)).logout({ logoutParams: { returnTo: `${window.location.origin}${CALLBACK_PATH}` } });
  return true;
}
